import { useState, useEffect } from 'react';
import { Flag } from '~/components/Flag';
import { formatNonTimezoneDate, formatDateToLocal, formatDateToPopulateDatePicker } from '~/utils';
import { PersonOutlineIcon } from '@applyboard/ui-icons';
import { LabelValue } from '~/components/uie-fix/LabelValue';
import { AccordionWithIntent } from '~/components/uie-fix/AccordionWithIntent';
import {
  Flex,
  Heading,
  Text,
  GridAlpha,
  Tag,
  HelpText,
  Button,
  TextInput,
  DatePicker,
  Checkbox,
  Select,
  useToasts,
} from '@applyboard/crystal-ui';
import { useAppProcessing } from '~/pages/ApplicationDetails/context';
import { ApplicantAddress, StatusAndCitizenship, StudentPersonaInformation } from '~/data/useApplication';
import { COUNTRY_NAMES, CountryIsoCode, GENDER_NAMES, LANGUAGE_NAMES } from '~/constants';

import { Card } from './Card';
import { AttachedDocuments } from './shared/AttachedDocuments';
import { VisaOutcomeRequest } from '../ApplicationProcessing/VisaOutcome/VisaOutcomeRequest';
import { AttachedDocument } from './shared/AttachedDocument';
import { useUpdatePersonalInformation } from '../../data/useUpdatePersonalInformation';
import type { UpdatePersonalInformationParams } from '../../data/useUpdatePersonalInformation';
import { validateAddress, validatePersonalInformation } from './ApplicantInformation.validation';

// Date validation constants for DatePicker components
const DATE_OF_BIRTH_MIN_DATE = new Date(1900, 0, 1).toISOString();
const DATE_OF_BIRTH_MAX_DATE = new Date().toISOString();
const PASSPORT_EXPIRY_MIN_DATE = new Date(new Date().setHours(0, 0, 0, 0)).toISOString();
const PASSPORT_EXPIRY_MAX_DATE = new Date(2100, 0, 1).toISOString();

// Create country options sorted alphabetically
const COUNTRY_OPTIONS = Object.entries(COUNTRY_NAMES)
  .map(([code, name]) => ({
    value: code as CountryIsoCode,
    label: name,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

// Create gender options sorted alphabetically
const GENDER_OPTIONS = Object.entries(GENDER_NAMES)
  .map(([code, name]) => ({
    value: code,
    label: name,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

// Create language options sorted alphabetically
const LANGUAGE_OPTIONS = Object.entries(LANGUAGE_NAMES)
  .map(([code, name]) => ({
    value: code,
    label: name,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

function ApplicantInfo({
  isEditing,
  editedData,
  onChange,
  updatedAt,
  updatedByRole,
  editButton,
}: {
  isEditing?: boolean;
  editedData?: Record<string, string>;
  onChange?: (field: string, value: string) => void;
  updatedAt?: string;
  updatedByRole?: string;
  editButton?: React.ReactNode;
}) {
  const {
    application: { applicant, statusAndCitizenship, studentPersonaInformation },
  } = useAppProcessing();
  const shouldRenderAIMLFeature =
    Boolean(statusAndCitizenship.documents[0]) && Boolean(statusAndCitizenship.documents[0].documentReviewSuggestion);
  const isPassportNumberNotAvailable = String(statusAndCitizenship.passportNumber) === 'NOT_AVAILABLE';
  const isPassportExpiryDateNotAvailable = String(statusAndCitizenship.passportExpiryDate) === 'NOT_AVAILABLE';

  const getStudentPersonaFields = (studentpersonaInformation?: StudentPersonaInformation): React.ReactNode[] => {
    const nodes: React.ReactNode[] = [];
    if (studentpersonaInformation?.personalInformation) {
      const { previousName, preferredName, firstLanguageSpoken } = studentpersonaInformation.personalInformation;
      const firstLanguageSpokenName = LANGUAGE_NAMES?.[firstLanguageSpoken as keyof typeof LANGUAGE_NAMES] || '--';

      nodes.push(<LabelValue key="previousName" label="PREVIOUS NAME" value={previousName || '--'} />);
      nodes.push(<LabelValue key="preferredName" label="PREFERRED NAME" value={preferredName || '--'} />);
      nodes.push(
        <LabelValue key="firstLanguageSpoken" label="FIRST LANGUAGE SPOKEN" value={firstLanguageSpokenName} />
      );
    }

    return nodes;
  };

  const formattedUpdatedAt = updatedAt ? formatDateToLocal(new Date(updatedAt)) : undefined;

  if (isEditing && editedData && onChange) {
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Heading level={4} variant="titleS">
            Personal Information
          </Heading>
          {editButton}
        </div>
        {formattedUpdatedAt && (
          <Text variant="bodyS" contrast="mid">
            Updated on {formattedUpdatedAt}
            {updatedByRole && <> by {updatedByRole}</>}
          </Text>
        )}
        <GridAlpha rows="repeat(3, auto)" columnGap={2} rowGap={6} columns="repeat(3, 1fr)">
          <TextInput
            label="GIVEN NAME"
            value={editedData.givenName}
            onChange={(value: string) => onChange('givenName', value)}
            required
          />
          <TextInput
            label="FAMILY NAME"
            value={editedData.familyName}
            onChange={(value: string) => onChange('familyName', value)}
          />
          <TextInput
            label="MIDDLE NAME"
            value={editedData.middleName}
            onChange={(value: string) => onChange('middleName', value)}
          />
          <DatePicker
            label="DATE OF BIRTH"
            value={editedData.dateOfBirth}
            onChange={(value: string) => onChange('dateOfBirth', value)}
            granularity="day"
            minDate={DATE_OF_BIRTH_MIN_DATE}
            maxDate={DATE_OF_BIRTH_MAX_DATE}
            required
          />
          <Select
            label="GENDER"
            value={editedData.gender}
            onChange={(value: string) => onChange('gender', value)}
            appearance="styled"
            required
          >
            {GENDER_OPTIONS.map((option) => (
              <Select.Option key={option.value} value={option.value} label={option.label} />
            ))}
          </Select>
          <Select
            label="NATIONALITY"
            value={editedData.nationality}
            onChange={(value: string) => onChange('nationality', value)}
            appearance="styled"
            required
          >
            {COUNTRY_OPTIONS.map((option) => (
              <Select.Option key={option.value} value={option.value} label={option.label} />
            ))}
          </Select>
          <TextInput
            label="PREVIOUS NAME"
            value={editedData.previousName}
            onChange={(value: string) => onChange('previousName', value)}
          />
          <TextInput
            label="PREFERRED NAME"
            value={editedData.preferredName}
            onChange={(value: string) => onChange('preferredName', value)}
          />
          <Select
            label="FIRST LANGUAGE SPOKEN"
            value={editedData.firstLanguageSpoken}
            onChange={(value: string) => onChange('firstLanguageSpoken', value)}
            appearance="styled"
          >
            {LANGUAGE_OPTIONS.map((option) => (
              <Select.Option key={option.value} value={option.value} label={option.label} />
            ))}
          </Select>
          {statusAndCitizenship.passportId && (
            <>
              <TextInput
                label="PASSPORT NUMBER"
                value={editedData.passportNumber}
                onChange={(value: string) => onChange('passportNumber', value)}
                required
              />
              <DatePicker
                label="DATE OF EXPIRY"
                value={editedData.passportExpiryDate}
                onChange={(value: string) => onChange('passportExpiryDate', value)}
                granularity="day"
                minDate={PASSPORT_EXPIRY_MIN_DATE}
                maxDate={PASSPORT_EXPIRY_MAX_DATE}
                required
              />
            </>
          )}
        </GridAlpha>
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Heading level={4} variant="titleS">
          Personal Information
        </Heading>
        {editButton}
      </div>
      {formattedUpdatedAt && (
        <Text variant="bodyS" contrast="mid">
          Updated on {formattedUpdatedAt}
          {updatedByRole && <> by {updatedByRole}</>}
        </Text>
      )}
      {shouldRenderAIMLFeature ? (
        <>
          <GridAlpha rows="repeat(3, auto)" columnGap={2} rowGap={6} columns="repeat(3, 1fr)">
            <LabelValue
              label="GIVEN NAME(S)"
              value={applicant.middleName ? `${applicant.givenName} ${applicant.middleName}` : applicant.givenName}
            />
            <LabelValue label="FAMILY NAME" value={applicant.familyName || '--'} />
            <LabelValue label="DATE OF BIRTH" value={formatNonTimezoneDate(new Date(applicant.dateOfBirth))} />
            <LabelValue
              label="NATIONALITY"
              value={applicant.nationality.nationalityLabel}
              icon={<Flag height={10} nationalityCode={applicant.nationality.alpha2Code} />}
            />
            <LabelValue label="GENDER" value={applicant.gender.name} />

            <LabelValue
              label="PASSPORT NUMBER"
              value={isPassportNumberNotAvailable ? 'Not available' : statusAndCitizenship.passportNumber}
            />
            <LabelValue
              label="DATE OF EXPIRY"
              value={
                isPassportExpiryDateNotAvailable
                  ? 'Not available'
                  : formatNonTimezoneDate(new Date(statusAndCitizenship.passportExpiryDate))
              }
            />
            {getStudentPersonaFields(studentPersonaInformation)}
          </GridAlpha>
          <GridAlpha>
            <LabelValue label="SUPPORTING DOCUMENTS" value="Passport" />
          </GridAlpha>
        </>
      ) : (
        <GridAlpha rows="repeat(3, auto)" columnGap={2} rowGap={6} columns="repeat(3, 1fr)">
          <LabelValue label="GIVEN NAME" value={applicant.givenName} />
          <LabelValue label="FAMILY NAME" value={applicant.familyName || '--'} />
          <LabelValue label="MIDDLE NAME" value={applicant.middleName || '--'} />
          <LabelValue label="DATE OF BIRTH" value={formatNonTimezoneDate(new Date(applicant.dateOfBirth))} />
          <LabelValue label="GENDER" value={applicant.gender.name} />
          <LabelValue
            label="NATIONALITY"
            value={applicant.nationality.nationalityLabel}
            icon={<Flag height={10} nationalityCode={applicant.nationality.alpha2Code} />}
          />
          {getStudentPersonaFields(studentPersonaInformation)}
        </GridAlpha>
      )}
    </>
  );
}
function AddressDetails(props: {
  heading: string;
  address: ApplicantAddress;
  isEditing?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editedAddress?: any;
  onChange?: (field: string, value: string) => void;
  updatedAt?: string;
  updatedByRole?: string;
  editButton?: React.ReactNode;
}) {
  const { address, heading, isEditing, editedAddress, onChange, updatedAt, updatedByRole, editButton } = props;
  const formattedUpdatedAt = updatedAt ? formatDateToLocal(new Date(updatedAt)) : undefined;

  if (isEditing && editedAddress && onChange) {
    return (
      <Flex gap={8} direction="column" role="region" aria-describedby={heading}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Heading level={4} variant="titleS">
            {heading}
          </Heading>
          {editButton}
        </div>
        {formattedUpdatedAt && (
          <Text variant="bodyS" contrast="mid">
            Updated on {formattedUpdatedAt}
            {updatedByRole && <> by {updatedByRole}</>}
          </Text>
        )}
        <GridAlpha rows="repeat(1, auto)" columnGap={2} rowGap={6} columns="repeat(3, 1fr)">
          <GridAlpha.Item rowStart={1} columnStart={1} columnEnd={2}>
            <Select
              label="COUNTRY"
              value={editedAddress.country}
              onChange={(value: string) => onChange('country', value)}
              appearance="styled"
              required
            >
              {COUNTRY_OPTIONS.map((option) => (
                <Select.Option key={option.value} value={option.value} label={option.label} />
              ))}
            </Select>
          </GridAlpha.Item>
          <GridAlpha.Item rowStart={2} columnStart={1} columnEnd={1}>
            <TextInput
              label="ADDRESS LINE 1"
              value={editedAddress.addressLine1}
              onChange={(value: string) => onChange('addressLine1', value)}
              required
            />
          </GridAlpha.Item>
          <GridAlpha.Item rowStart={2} columnStart={2} columnEnd={2}>
            <TextInput
              label="ADDRESS LINE 2"
              value={editedAddress.addressLine2}
              onChange={(value: string) => onChange('addressLine2', value)}
            />
          </GridAlpha.Item>
          <GridAlpha.Item rowStart={3} columnStart={1} columnEnd={1}>
            <TextInput
              label="CITY"
              value={editedAddress.city}
              onChange={(value: string) => onChange('city', value)}
              required
            />
          </GridAlpha.Item>
          <GridAlpha.Item rowStart={3} columnStart={2} columnEnd={2}>
            <TextInput
              label="PROVINCE/STATE/AREA"
              value={editedAddress.province}
              onChange={(value: string) => onChange('province', value)}
              required
            />
          </GridAlpha.Item>
          <GridAlpha.Item rowStart={4} columnStart={1} columnEnd={2}>
            <TextInput
              label="POSTAL/ZIP CODE"
              value={editedAddress.postalCode}
              onChange={(value: string) => onChange('postalCode', value)}
            />
          </GridAlpha.Item>
        </GridAlpha>
      </Flex>
    );
  }

  return (
    <Flex gap={8} direction="column" role="region" aria-describedby={heading}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Heading level={4} variant="titleS">
          {heading}
        </Heading>
        {editButton}
      </div>
      {formattedUpdatedAt && (
        <Text variant="bodyS" contrast="mid">
          Updated on {formattedUpdatedAt}
          {updatedByRole && <> by {updatedByRole}</>}
        </Text>
      )}
      <GridAlpha rows="repeat(1, auto)" columnGap={2} rowGap={6} columns="repeat(3, 1fr)">
        <GridAlpha.Item rowStart={1} columnStart={1} columnEnd={2}>
          <LabelValue label="COUNTRY" value={address.country.countryLabel} />
        </GridAlpha.Item>
        <GridAlpha.Item rowStart={2} columnStart={1} columnEnd={1}>
          <LabelValue label="ADDRESS LINE 1" value={address.addressLine1} />
        </GridAlpha.Item>
        <GridAlpha.Item rowStart={2} columnStart={2} columnEnd={2}>
          <LabelValue label="ADDRESS LINE 2" value={address.addressLine2 || '--'} />
        </GridAlpha.Item>
        <GridAlpha.Item rowStart={3} columnStart={1} columnEnd={1}>
          <LabelValue label="CITY" value={address.city} />
        </GridAlpha.Item>
        <GridAlpha.Item rowStart={3} columnStart={2} columnEnd={2}>
          <LabelValue label="PROVINCE/STATE/AREA" value={address.province} />
        </GridAlpha.Item>
        <GridAlpha.Item rowStart={4} columnStart={1} columnEnd={2}>
          <LabelValue label="POSTAL/ZIP CODE" value={address.postalCode || '--'} />
        </GridAlpha.Item>
      </GridAlpha>
    </Flex>
  );
}

type AiExtractionScenario = {
  title: string;
  tagText: string;
  intent: 'positive' | 'negative' | 'warning' | 'secondary';
  message: string;
};

type AiExtractionScenarios = {
  CONSISTENT: AiExtractionScenario;
  NEEDS_REVIEW: AiExtractionScenario;
  UNABLE_TO_CHECK: AiExtractionScenario;
};

function AIExtractedPassportReport(props: { statusAndCitizenship: StatusAndCitizenship }) {
  const { statusAndCitizenship } = props;
  const latestPassport = statusAndCitizenship?.documents[0];

  if (!latestPassport?.documentReviewSuggestion) {
    return;
  } else {
    const documentReviewSuggestion = latestPassport.documentReviewSuggestion;

    const aiExtractionScenarios: AiExtractionScenarios = {
      CONSISTENT: {
        title: 'Pass',
        tagText: 'Consistent',
        intent: 'positive',
        message: documentReviewSuggestion.message,
      },
      NEEDS_REVIEW: {
        title: 'Needs Review',
        tagText: 'Needs Review',
        intent: 'warning',
        message: documentReviewSuggestion.message,
      },
      UNABLE_TO_CHECK: {
        title: 'Unable to Check',
        tagText: 'Unable to Check',
        intent: 'secondary',
        message: documentReviewSuggestion.message,
      },
    };

    const currentAiScenario = aiExtractionScenarios[documentReviewSuggestion.status];

    return (
      <Flex gap={4} direction="column">
        <Text variant="labelM">Attached file</Text>
        <Flex gap={6} direction="column">
          <AttachedDocument attachmentDocument={latestPassport} key={latestPassport.key} />
        </Flex>
        <Text variant="labelM">Document health</Text>
        <AccordionWithIntent
          id="extracted_info_status_accordion"
          title={currentAiScenario.title}
          intent={currentAiScenario.intent}
        >
          <Flex direction="column" align="start" gap={2}>
            <HelpText>
              <span
                style={{
                  fontSize: '12px',
                  fontFamily: 'Montserrat',
                }}
              >
                DATA ACCURACY
              </span>
            </HelpText>
            <Tag intent={currentAiScenario.intent} size="md">
              {currentAiScenario.tagText}
            </Tag>
            <Text variant="bodyM" intent="secondary">
              {currentAiScenario.message}
            </Text>
          </Flex>
        </AccordionWithIntent>
      </Flex>
    );
  }
}

export function ApplicantInformation() {
  const {
    application: { id: applicationId, applicant, statusAndCitizenship, studentPersonaInformation, submittedAt },
    entityHistoryMap,
  } = useAppProcessing();
  const studentId = studentPersonaInformation?.id;
  const isMailingAddressSameAsResidential = applicant.addresses.isMailingAddressSameAsResidential;
  const shouldRenderAIMLFeature =
    Boolean(statusAndCitizenship.documents[0]) && Boolean(statusAndCitizenship.documents[0].documentReviewSuggestion);

  const updateMutation = useUpdatePersonalInformation();
  const toast = useToasts();
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addressesAreSame, setAddressesAreSame] = useState(isMailingAddressSameAsResidential);

  const isPassportNumberNotAvailable = String(statusAndCitizenship.passportNumber) === 'NOT_AVAILABLE';
  const isPassportExpiryDateNotAvailable = String(statusAndCitizenship.passportExpiryDate) === 'NOT_AVAILABLE';

  const [editedData, setEditedData] = useState({
    givenName: applicant.givenName,
    middleName: applicant.middleName || '',
    familyName: applicant.familyName || '',
    dateOfBirth: formatDateToPopulateDatePicker(applicant.dateOfBirth),
    nationality: applicant.nationality.alpha2Code,
    gender: applicant.gender.code,
    passportNumber: isPassportNumberNotAvailable ? '' : statusAndCitizenship.passportNumber || '',
    passportExpiryDate: isPassportExpiryDateNotAvailable
      ? ''
      : formatDateToPopulateDatePicker(statusAndCitizenship.passportExpiryDate),
    previousName: studentPersonaInformation?.personalInformation?.previousName || '',
    preferredName: studentPersonaInformation?.personalInformation?.preferredName || '',
    firstLanguageSpoken: studentPersonaInformation?.personalInformation?.firstLanguageSpoken || '',
    residentialAddress: {
      addressLine1: applicant.addresses.residentialAddress.addressLine1,
      addressLine2: applicant.addresses.residentialAddress.addressLine2 || '',
      city: applicant.addresses.residentialAddress.city,
      province: applicant.addresses.residentialAddress.province,
      country: applicant.addresses.residentialAddress.country.alpha2Code,
      postalCode: applicant.addresses.residentialAddress.postalCode || '',
    },
    mailingAddress: {
      addressLine1: applicant.addresses.mailingAddress.addressLine1,
      addressLine2: applicant.addresses.mailingAddress.addressLine2 || '',
      city: applicant.addresses.mailingAddress.city,
      province: applicant.addresses.mailingAddress.province,
      country: applicant.addresses.mailingAddress.country.alpha2Code,
      postalCode: applicant.addresses.mailingAddress.postalCode || '',
    },
  });

  // Sync local state with props when they change (e.g., after successful mutation)
  // Only update when not editing to avoid overwriting user's in-progress changes
  useEffect(() => {
    if (!isEditing) {
      setEditedData({
        givenName: applicant.givenName || '',
        middleName: applicant.middleName || '',
        familyName: applicant.familyName || '',
        dateOfBirth: formatDateToPopulateDatePicker(applicant.dateOfBirth),
        nationality: applicant.nationality?.alpha2Code || '',
        gender: applicant.gender?.code || '',
        passportNumber: isPassportNumberNotAvailable ? '' : statusAndCitizenship.passportNumber || '',
        passportExpiryDate: isPassportExpiryDateNotAvailable
          ? ''
          : formatDateToPopulateDatePicker(statusAndCitizenship.passportExpiryDate),
        previousName: studentPersonaInformation?.personalInformation?.previousName || '',
        preferredName: studentPersonaInformation?.personalInformation?.preferredName || '',
        firstLanguageSpoken: studentPersonaInformation?.personalInformation?.firstLanguageSpoken || '',
        residentialAddress: {
          addressLine1: applicant.addresses.residentialAddress.addressLine1 || '',
          addressLine2: applicant.addresses.residentialAddress.addressLine2 || '',
          city: applicant.addresses.residentialAddress.city || '',
          province: applicant.addresses.residentialAddress.province || '',
          country: applicant.addresses.residentialAddress.country?.alpha2Code || '',
          postalCode: applicant.addresses.residentialAddress.postalCode || '',
        },
        mailingAddress: {
          addressLine1: applicant.addresses.mailingAddress.addressLine1 || '',
          addressLine2: applicant.addresses.mailingAddress.addressLine2 || '',
          city: applicant.addresses.mailingAddress.city || '',
          province: applicant.addresses.mailingAddress.province || '',
          country: applicant.addresses.mailingAddress.country?.alpha2Code || '',
          postalCode: applicant.addresses.mailingAddress.postalCode || '',
        },
      });
    }
  }, [
    applicant,
    statusAndCitizenship,
    studentPersonaInformation,
    isEditing,
    isPassportNumberNotAvailable,
    isPassportExpiryDateNotAvailable,
  ]);

  const handleFieldChange = (field: string, value: string) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  };

  const handleResidentialAddressChange = (field: string, value: string) => {
    setEditedData((prev) => {
      const updatedResidentialAddress = { ...prev.residentialAddress, [field]: value };
      // If user has chosen to keep addresses the same, update both
      if (addressesAreSame) {
        return {
          ...prev,
          residentialAddress: updatedResidentialAddress,
          mailingAddress: updatedResidentialAddress,
        };
      }
      return {
        ...prev,
        residentialAddress: updatedResidentialAddress,
      };
    });
  };

  const handleMailingAddressChange = (field: string, value: string) => {
    setEditedData((prev) => ({
      ...prev,
      mailingAddress: { ...prev.mailingAddress, [field]: value },
    }));
  };

  const handleAddressesSameChange = (checked: boolean) => {
    setAddressesAreSame(checked);
    // If checking the box, sync mailing address with residential
    if (checked) {
      setEditedData((prev) => ({
        ...prev,
        mailingAddress: prev.residentialAddress,
      }));
    }
  };

  // Helper to ensure date is in YYYY-MM-DD format
  const toDateOnly = (value: string) => {
    if (!value) return null;
    const date = new Date(value);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return null;
    }
    return date.toISOString().slice(0, 10);
  };

  const handleSave = async () => {
    // Validate personal information
    const validation = validatePersonalInformation(
      {
        givenName: editedData.givenName,
        dateOfBirth: editedData.dateOfBirth,
        nationality: editedData.nationality,
        gender: editedData.gender,
        passportNumber: editedData.passportNumber,
        passportExpiryDate: editedData.passportExpiryDate,
      },
      { requirePassport: !!statusAndCitizenship.passportId }
    );

    // If there are validation errors, show them all
    if (!validation.isValid && validation.errors) {
      validation.errors.forEach((error) => {
        toast.negative(new Error(error));
      });
      return;
    }

    try {
      // Conditionally construct passport object
      const passportData =
        statusAndCitizenship.passportId && studentId
          ? {
              passportId: statusAndCitizenship.passportId,
              studentId,
              passportNumber: editedData.passportNumber || null,
              expiryDate: toDateOnly(editedData.passportExpiryDate),
              nationality: editedData.nationality || null,
            }
          : undefined;

      // Conditionally construct student personal information object
      const studentPersonalInformationData = studentId
        ? {
            studentId,
            previousName: editedData.previousName || null,
            preferredName: editedData.preferredName || null,
            firstLanguageSpoken: editedData.firstLanguageSpoken || null,
          }
        : undefined;

      const payload: UpdatePersonalInformationParams = {
        id: applicationId,
        personalInformation: {
          basicPersonalInformation: {
            givenName: editedData.givenName,
            middleName: editedData.middleName || null,
            familyName: editedData.familyName || null,
            dateOfBirth: toDateOnly(editedData.dateOfBirth),
            nationality: editedData.nationality,
            gender: editedData.gender,
          },
          ...(passportData && { passport: passportData }),
          ...(studentPersonalInformationData && { studentPersonalInformation: studentPersonalInformationData }),
        },
      };

      await updateMutation.mutateAsync(payload);
      toast.positive('Personal information updated successfully');
      setIsEditing(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update personal information';
      toast.negative(new Error(errorMessage));
    }
  };

  const handleSaveAddress = async () => {
    // Validate residential address
    const residentialValidation = validateAddress(editedData.residentialAddress, 'residential');
    if (!residentialValidation.isValid) {
      toast.negative(new Error(residentialValidation.errorMessage || 'Validation failed'));
      return;
    }

    // Validate mailing address if it's different from residential
    if (!addressesAreSame) {
      const mailingValidation = validateAddress(editedData.mailingAddress, 'mailing');
      if (!mailingValidation.isValid) {
        toast.negative(new Error(mailingValidation.errorMessage || 'Validation failed'));
        return;
      }
    }

    try {
      // Helper to convert empty strings to null for optional fields
      const sanitizeAddress = (address: typeof editedData.residentialAddress) => ({
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2 || null,
        city: address.city,
        province: address.province,
        country: address.country,
        postalCode: address.postalCode || null,
      });

      const hasAddressChanged = (
        edited: typeof editedData.residentialAddress,
        original: typeof applicant.addresses.residentialAddress
      ) => {
        return (
          edited.addressLine1 !== (original.addressLine1 || '') ||
          edited.addressLine2 !== (original.addressLine2 || '') ||
          edited.city !== (original.city || '') ||
          edited.province !== (original.province || '') ||
          edited.country !== (original.country?.alpha2Code || '') ||
          edited.postalCode !== (original.postalCode || '')
        );
      };

      const residentialChanged = hasAddressChanged(
        editedData.residentialAddress,
        applicant.addresses.residentialAddress
      );
      const mailingChanged =
        !addressesAreSame && hasAddressChanged(editedData.mailingAddress, applicant.addresses.mailingAddress);

      const sameAddressSettingChanged = addressesAreSame !== isMailingAddressSameAsResidential;

      if (residentialChanged || mailingChanged || sameAddressSettingChanged) {
        // Always send both addresses with all fields to ensure null values are properly sent
        const sanitizedResidentialAddress = sanitizeAddress(editedData.residentialAddress);
        const sanitizedMailingAddress = addressesAreSame
          ? sanitizeAddress(editedData.residentialAddress)
          : sanitizeAddress(editedData.mailingAddress);

        const payload: UpdatePersonalInformationParams = {
          id: applicationId,
          personalInformation: {
            addresses: {
              residentialAddress: sanitizedResidentialAddress,
              mailingAddress: sanitizedMailingAddress,
            },
          },
        };

        await updateMutation.mutateAsync(payload);
      }

      toast.positive('Address information updated successfully');
      setIsEditingAddress(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update address information';
      toast.negative(new Error(errorMessage));
    }
  };

  const handleCancel = () => {
    // Reset to original values
    setEditedData({
      givenName: applicant.givenName,
      middleName: applicant.middleName || '',
      familyName: applicant.familyName || '',
      dateOfBirth: formatDateToPopulateDatePicker(applicant.dateOfBirth),
      nationality: applicant.nationality.alpha2Code,
      gender: applicant.gender.code,
      passportNumber: isPassportNumberNotAvailable ? '' : statusAndCitizenship.passportNumber || '',
      passportExpiryDate: isPassportExpiryDateNotAvailable
        ? ''
        : formatDateToPopulateDatePicker(statusAndCitizenship.passportExpiryDate),
      previousName: studentPersonaInformation?.personalInformation?.previousName || '',
      preferredName: studentPersonaInformation?.personalInformation?.preferredName || '',
      firstLanguageSpoken: studentPersonaInformation?.personalInformation?.firstLanguageSpoken || '',
      residentialAddress: {
        addressLine1: applicant.addresses.residentialAddress.addressLine1,
        addressLine2: applicant.addresses.residentialAddress.addressLine2 || '',
        city: applicant.addresses.residentialAddress.city,
        province: applicant.addresses.residentialAddress.province,
        country: applicant.addresses.residentialAddress.country.alpha2Code,
        postalCode: applicant.addresses.residentialAddress.postalCode || '',
      },
      mailingAddress: {
        addressLine1: applicant.addresses.mailingAddress.addressLine1,
        addressLine2: applicant.addresses.mailingAddress.addressLine2 || '',
        city: applicant.addresses.mailingAddress.city,
        province: applicant.addresses.mailingAddress.province,
        country: applicant.addresses.mailingAddress.country.alpha2Code,
        postalCode: applicant.addresses.mailingAddress.postalCode || '',
      },
    });
    setIsEditing(false);
  };

  const handleCancelAddress = () => {
    setEditedData((prev) => ({
      ...prev,
      residentialAddress: {
        addressLine1: applicant.addresses.residentialAddress.addressLine1 || '',
        addressLine2: applicant.addresses.residentialAddress.addressLine2 || '',
        city: applicant.addresses.residentialAddress.city || '',
        province: applicant.addresses.residentialAddress.province || '',
        country: applicant.addresses.residentialAddress.country?.alpha2Code || '',
        postalCode: applicant.addresses.residentialAddress.postalCode || '',
      },
      mailingAddress: {
        addressLine1: applicant.addresses.mailingAddress.addressLine1 || '',
        addressLine2: applicant.addresses.mailingAddress.addressLine2 || '',
        city: applicant.addresses.mailingAddress.city || '',
        province: applicant.addresses.mailingAddress.province || '',
        country: applicant.addresses.mailingAddress.country?.alpha2Code || '',
        postalCode: applicant.addresses.mailingAddress.postalCode || '',
      },
    }));
    setAddressesAreSame(isMailingAddressSameAsResidential);
    setIsEditingAddress(false);
  };

  const personalInfoEntry = entityHistoryMap?.personalInformation;
  const isPersonalInfoUpdatedAfterSubmission =
    personalInfoEntry?.updatedAt && submittedAt && new Date(personalInfoEntry.updatedAt) > new Date(submittedAt);

  return (
    <>
      <Card
        id="applicant"
        icon={PersonOutlineIcon}
        title="Applicant Information"
        tag={
          shouldRenderAIMLFeature
            ? {
                text: 'This section is AI enabled',
                intent: 'primary',
              }
            : undefined
        }
      >
        <Flex gap={8} direction="column">
          <ApplicantInfo
            isEditing={isEditing}
            editedData={editedData as unknown as Record<string, string>}
            onChange={handleFieldChange}
            updatedAt={isPersonalInfoUpdatedAfterSubmission ? personalInfoEntry?.updatedAt : undefined}
            updatedByRole={isPersonalInfoUpdatedAfterSubmission ? personalInfoEntry?.updatedByRole : undefined}
            editButton={
              !isEditing && !isEditingAddress ? (
                <Button size="sm" variant="outlined" onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
              ) : undefined
            }
          />
          {isEditing && (
            <Flex gap={2} justify="end">
              <Button variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={updateMutation.isLoading}>
                {updateMutation.isLoading ? 'Saving...' : 'Save'}
              </Button>
            </Flex>
          )}
          <AIExtractedPassportReport statusAndCitizenship={statusAndCitizenship} />
          <AddressDetails
            heading="Residential Address"
            address={applicant.addresses.residentialAddress}
            isEditing={isEditingAddress}
            editedAddress={editedData.residentialAddress}
            onChange={handleResidentialAddressChange}
            updatedAt={entityHistoryMap?.addresses?.updatedAt}
            updatedByRole={entityHistoryMap?.addresses?.updatedByRole}
            editButton={
              !isEditing && !isEditingAddress ? (
                <Button size="sm" variant="outlined" onClick={() => setIsEditingAddress(true)}>
                  Edit
                </Button>
              ) : undefined
            }
          />
          {isEditingAddress && (
            <Checkbox
              value={addressesAreSame}
              onChange={handleAddressesSameChange}
              label="Mailing address is the same as residential address"
            />
          )}
          {!isEditingAddress && isMailingAddressSameAsResidential ? (
            <Text>Mailing address is the same as residential address.</Text>
          ) : (
            !addressesAreSame && (
              <AddressDetails
                heading="Mailing Address"
                address={applicant.addresses.mailingAddress}
                isEditing={isEditingAddress}
                editedAddress={editedData.mailingAddress}
                onChange={handleMailingAddressChange}
              />
            )
          )}
          {isEditingAddress && (
            <Flex gap={2} justify="end">
              <Button variant="outlined" onClick={handleCancelAddress}>
                Cancel
              </Button>
              <Button onClick={handleSaveAddress} disabled={updateMutation.isLoading}>
                {updateMutation.isLoading ? 'Saving...' : 'Save'}
              </Button>
            </Flex>
          )}
          {shouldRenderAIMLFeature && (
            <>
              <Heading level={4} variant="titleS">
                Status Information
              </Heading>
              <LabelValue label="CANADA STATUS" value={statusAndCitizenship.destinationCountryStatus.name} />
              <AttachedDocuments
                disableAdditionalRequest
                attachmentDocuments={Object.values(statusAndCitizenship.documents)}
                showTitle
                section="statusAndCitizenship"
                sectionReference={statusAndCitizenship.key}
                noRequestForMissingDocuments
                types={[
                  'DESTINATION_COUNTRY_STATUS',
                  'STUDY_PERMIT',
                  'WORK_PERMIT',
                  'VISITOR_TOURIST_VISA',
                  'VISA_OUTCOME',
                  'REFUGEE',
                  'REFUGEE_CLAIMANT',
                ]}
              />
              <VisaOutcomeRequest />
            </>
          )}
        </Flex>
      </Card>
    </>
  );
}
